import type { HTMLAttributes, ReactNode, RefObject } from 'react'
import { useState } from 'react'

import type { SerializedStyles } from '@emotion/react'
import { DialogContent } from '@reach/dialog'
import { useSpring } from '@react-spring/core'
import { animated } from '@react-spring/web'

import { ModalOverlay } from '../components'
import type { ModalA11yProps } from '../types'
import { animationConfig } from '../utils/animation-config'
import { getModalProps } from '../utils/get-modal-props'

import * as styles from './side-modal.styles'

type Props = {
  children: ReactNode
  onClickOutside?: () => void
  opened: boolean
  withoutOverlay?: boolean
  modalBoxRef?: RefObject<HTMLDivElement>
  modalBoxStyles?: SerializedStyles | SerializedStyles[]
  modalOverlayStyles?: SerializedStyles | SerializedStyles[]
  onClose?: () => void
} & ModalA11yProps &
  Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'style'>

const AnimatedDialogContent = animated(DialogContent)

export const SideModal = ({
  children,
  onClickOutside,
  opened,
  withoutOverlay = false,
  modalBoxRef,
  modalBoxStyles,
  modalOverlayStyles,
  onClose,
  ...rest
}: Props) => {
  const [bypassFocusLock, setBypassFocusLock] = useState(true)

  const modalAnimationStyles = useSpring({
    from: { transform: 'translate3d(100%, 0, 0)' },
    to: {
      transform: opened ? 'translate3d(0%, 0, 0)' : 'translate3d(100%, 0, 0)',
    },
    onRest: () => {
      // when modal is being opened we must disable focus lock to prevent issue with buggy slide animation
      // https://github.com/reach/reach-ui/issues/746
      setBypassFocusLock(!opened)
      if (!opened && onClose) {
        onClose()
      }
    },
    config: animationConfig,
  })

  const { a11yProps, restProps } = getModalProps(rest)

  return (
    <ModalOverlay
      css={modalOverlayStyles}
      dangerouslyBypassFocusLock={bypassFocusLock}
      data-testid="side-modal"
      hasOverlay={!withoutOverlay}
      isVisible={opened}
      onClickOutside={onClickOutside}
      {...restProps}
    >
      <>
        <AnimatedDialogContent
          ref={modalBoxRef}
          css={[styles.content, modalBoxStyles]}
          style={modalAnimationStyles}
          {...a11yProps}
        >
          {children}
        </AnimatedDialogContent>
      </>
    </ModalOverlay>
  )
}
